import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

// 교적관리 대시보드: /stats/overview 중 성도·출석·심방 통계만 사용.
type MonthCount = { month: string; count: number };

type RollOverview = {
  content: { visitationsTotal: number; visitationsThisMonth: number };
  congregation: {
    total: number;
    byStatus: { status: string; count: number }[];
    byBaptism: { baptism: string; count: number }[];
    byGender: { gender: string; count: number }[];
    growth: MonthCount[];
  };
  attendance: {
    weekly: { date: string; present: number; online: number; absent: number }[];
    overallRate: number;
  };
};

const DOT_COLORS = ['#1d4ed8', '#047857', '#b45309', '#be185d', '#6d28d9', '#0f766e'];

function monthLabel(month: string) {
  return `${Number(month.split('-')[1])}월`;
}

function dateLabel(date: string) {
  const [, m, d] = date.split('-');
  return `${Number(m)}/${Number(d)}`;
}

function ShareCard({ title, rows }: { title: string; rows: { label: string; count: number }[] }) {
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  return (
    <div className="manage-side-card">
      <div className="manage-side-card-head">{title}</div>
      <div className="manage-side-card-body">
        {rows.length === 0 && <p className="manage-side-empty">등록된 성도가 없습니다.</p>}
        {rows.map((r, index) => {
          const percent = total > 0 ? Math.round((r.count / total) * 100) : 0;
          return (
            <div className="manage-share-row" key={r.label}>
              <div className="manage-share-row-top">
                <b>{r.label || '미입력'}</b>
                <span>{r.count}명 · {percent}%</span>
              </div>
              <div className="manage-share-bar-wrap">
                <div
                  className="manage-share-bar-fill"
                  style={{ width: `${percent}%`, background: DOT_COLORS[index % DOT_COLORS.length] }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ManageRollDashboard({ authChurch }: { authChurch: string }) {
  const [data, setData] = useState<RollOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${MainURL}/stats/overview`, { params: { authChurch } })
      .then((res) => setData(res.data?.data ?? null))
      .catch((err) => {
        console.error('GET /stats/overview', err);
        setError('대시보드 데이터를 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  }, [authChurch]);

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">대시보드</h2>
          <p className="manage-panel-desc">성도·출석·심방 현황을 한눈에 확인합니다.</p>
        </div>
      </div>

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && data && (
        <>
          <div className="manage-stats">
            <div className="manage-stat-card">
              <span>성도 수</span>
              <strong>{data.congregation.total}명</strong>
            </div>
            <div className="manage-stat-card">
              <span>최근 8주 출석률</span>
              <strong>{Math.round(data.attendance.overallRate * 100)}%</strong>
            </div>
            <div className="manage-stat-card">
              <span>이번달 심방</span>
              <strong>{data.content.visitationsThisMonth}건</strong>
            </div>
            <div className="manage-stat-card">
              <span>심방 누적</span>
              <strong>{data.content.visitationsTotal}건</strong>
            </div>
          </div>

          <div className="manage-grid-2col">
            <div className="manage-side-stack">
              <section className="manage-card-block">
                <div className="manage-block-head">
                  <h3 className="manage-block-title">주별 출석 (현장 + 온라인)</h3>
                </div>
                {data.attendance.weekly.length === 0 ? (
                  <p className="manage-side-empty" style={{ padding: 16 }}>아직 출석 기록이 없습니다.</p>
                ) : (
                  <div className="manage-mini-chart" style={{ padding: '16px 18px' }}>
                    {data.attendance.weekly.map((w, index) => {
                      const attended = w.present + w.online;
                      const max = Math.max(1, ...data.attendance.weekly.map((r) => r.present + r.online));
                      return (
                        <div className="manage-mini-chart-col" key={w.date}>
                          <div
                            className={`manage-mini-chart-bar${index === data.attendance.weekly.length - 1 ? ' is-current' : ''}`}
                            style={{ height: `${Math.max(4, (attended / max) * 100)}%` }}
                            title={`${w.date} 현장 ${w.present} · 온라인 ${w.online} · 결석 ${w.absent}`}
                          />
                          <span className="manage-mini-chart-label">{dateLabel(w.date)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="manage-card-block">
                <div className="manage-block-head">
                  <h3 className="manage-block-title">성도 등록 추이</h3>
                </div>
                <div className="manage-mini-chart" style={{ padding: '16px 18px' }}>
                  {data.congregation.growth.map((m, index) => {
                    const max = Math.max(1, ...data.congregation.growth.map((r) => r.count));
                    return (
                      <div className="manage-mini-chart-col" key={m.month}>
                        <div
                          className={`manage-mini-chart-bar${index === data.congregation.growth.length - 1 ? ' is-current' : ''}`}
                          style={{ height: `${Math.max(4, (m.count / max) * 100)}%` }}
                          title={`${monthLabel(m.month)} ${m.count}명`}
                        />
                        <span className="manage-mini-chart-label">{monthLabel(m.month)}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            <div className="manage-side-stack">
              <ShareCard
                title="👥 성도 상태"
                rows={data.congregation.byStatus.map((r) => ({ label: r.status, count: r.count }))}
              />
              <ShareCard
                title="💧 세례 여부"
                rows={data.congregation.byBaptism.map((r) => ({ label: r.baptism, count: r.count }))}
              />
              <ShareCard
                title="🧑‍🤝‍🧑 성별"
                rows={data.congregation.byGender.map((r) => ({ label: r.gender, count: r.count }))}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
